import { ReactNode, useEffect, useMemo, useState } from 'react';
import { apiService } from '@/services/api.service';
import styles from './MinecraftText.module.scss';

// Couleurs Minecraft BEDROCK (§0-9a-f + couleurs de matériaux §g-§u).
// NB : en Bedrock, §m = Material Redstone et §n = Material Copper (des COULEURS),
// il n'existe ni barré ni souligné via les codes §.
const COLORS: Record<string, string> = {
  '0': '#000000', '1': '#0000AA', '2': '#00AA00', '3': '#00AAAA',
  '4': '#AA0000', '5': '#AA00AA', '6': '#FFAA00', '7': '#AAAAAA',
  '8': '#555555', '9': '#5555FF', 'a': '#55FF55', 'b': '#55FFFF',
  'c': '#FF5555', 'd': '#FF55FF', 'e': '#FFFF55', 'f': '#FFFFFF',
  // Couleurs de matériaux Bedrock
  'g': '#DDD605', 'h': '#E3D4D1', 'i': '#CECACA', 'j': '#443A3B',
  'm': '#971607', 'n': '#B4684D', 'p': '#DEB12D', 'q': '#47A036',
  's': '#2CBAA8', 't': '#21497B', 'u': '#9A5CC6',
};

interface Ctx {
  hostname?: string;
  player?: string;
  faction?: string;
  message?: string;
}

const DEFAULT_CTX: Required<Ctx> = { hostname: 'mining', player: 'Steve', faction: 'MaFaction', message: 'Salut !' };

/** Rend un texte au format chat Minecraft (§couleurs/formats + placeholders + glyphs custom du resource pack). */
export function MinecraftText({ text, context }: { text: string; context?: Ctx }) {
  const resolved = useMemo(() => {
    const c = { ...DEFAULT_CTX, ...context };
    return (text ?? '')
      .replaceAll('{hostname}', c.hostname)
      .replaceAll('{player}', c.player)
      .replaceAll('{faction}', c.faction)
      .replaceAll('{message}', c.message);
  }, [text, context]);

  // Pages de glyphs (high byte) utilisées dans le texte.
  const pagesKey = useMemo(() => {
    const set = new Set<string>();
    for (const ch of resolved) {
      const cp = ch.codePointAt(0)!;
      if (cp >= 0xe000 && cp <= 0xffff) set.add(((cp >> 8) & 0xff).toString(16).toUpperCase().padStart(2, '0'));
    }
    return [...set].sort().join(',');
  }, [resolved]);

  const [atlas, setAtlas] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!pagesKey) { setAtlas({}); return; }
    const pages = pagesKey.split(',');
    let active = true;
    const created: string[] = [];
    (async () => {
      const entries = await Promise.all(pages.map(async (p) => [p, await apiService.getGlyphAtlasBlobUrl(p)] as const));
      if (!active) { entries.forEach(([, u]) => u && URL.revokeObjectURL(u)); return; }
      const map: Record<string, string> = {};
      for (const [p, u] of entries) if (u) { map[p] = u; created.push(u); }
      setAtlas(map);
    })();
    return () => { active = false; created.forEach((u) => URL.revokeObjectURL(u)); };
  }, [pagesKey]);

  const nodes = useMemo(() => renderRuns(resolved, atlas), [resolved, atlas]);
  return <span className={styles.mc}>{nodes}</span>;
}

function renderRuns(text: string, atlas: Record<string, string>): ReactNode[] {
  const out: ReactNode[] = [];
  let color = '#FFFFFF';
  let bold = false, italic = false;
  let buf = '';
  let key = 0;
  const styleOf = (): React.CSSProperties => ({
    color,
    fontWeight: bold ? 700 : 400,
    fontStyle: italic ? 'italic' : 'normal',
  });
  const flush = () => { if (buf) { out.push(<span key={key++} style={styleOf()}>{buf}</span>); buf = ''; } };

  const chars = [...text];
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i]!;
    if (ch === '§' && i + 1 < chars.length) {
      const code = chars[++i]!.toLowerCase();
      // En Bedrock, un code couleur réinitialise le style (gras/italique).
      if (COLORS[code]) { flush(); color = COLORS[code]; bold = italic = false; }
      else if (code === 'l') { flush(); bold = true; }
      else if (code === 'o') { flush(); italic = true; }
      else if (code === 'r') { flush(); color = '#FFFFFF'; bold = italic = false; }
      // §k (obfuscated) et codes inconnus : ignorés visuellement
      continue;
    }
    const cp = ch.codePointAt(0)!;
    if (cp >= 0xe000 && cp <= 0xffff) {
      flush();
      const page = ((cp >> 8) & 0xff).toString(16).toUpperCase().padStart(2, '0');
      const idx = cp & 0xff;
      const rowc = idx >> 4;
      const colc = idx & 0xf;
      const url = atlas[page];
      if (url) {
        out.push(<span key={key++} className={styles.glyph} style={{ backgroundImage: `url(${url})`, backgroundPosition: `${(colc / 15) * 100}% ${(rowc / 15) * 100}%` }} />);
      } else {
        out.push(<span key={key++} className={styles.glyphMissing} title={`U+${cp.toString(16).toUpperCase()}`}>▯</span>);
      }
      continue;
    }
    buf += ch;
  }
  flush();
  return out;
}
